/**
 * English Data Service - Unified Data Layer
 * 
 * Provides a unified interface for English learning data with feature flags.
 * Supports gradual migration from Supabase to A2A backend.
 * 
 * Feature Flags:
 * - USE_A2A_BACKEND: Enable A2A backend (default: true)
 * - FALLBACK_TO_SUPABASE: Fallback to Supabase on A2A failure (default: true)
 */

import { FeatureFlags } from '../utils/featureFlags';
import a2aEnglishService from '../api/services/a2aEnglishService';

// Import existing Supabase services (old)
import * as supabaseServices from './supabaseService';

/**
 * English Data Service Class
 */
class EnglishDataService {
  constructor() {
    this.useA2A = FeatureFlags.USE_A2A_BACKEND;
    this.fallbackToSupabase = FeatureFlags.FALLBACK_TO_SUPABASE;
    this.enableLogging = FeatureFlags.ENABLE_LOGGING;
    
    this.log('EnglishDataService initialized', {
      useA2A: this.useA2A,
      fallbackToSupabase: this.fallbackToSupabase
    });
  }

  /**
   * Internal logging method
   */
  log(message, data = null) {
    if (this.enableLogging) {
      console.log(`[EnglishDataService] ${message}`, data || '');
    }
  }

  /**
   * Internal error logging method
   * Always logs errors (even in production) for debugging
   */
  logError(message, error) {
    // Always log errors, even in production, for debugging A2A issues
    console.error(`[EnglishDataService] ${message}`, error);
    // Also log error details for production debugging
    if (error && typeof error === 'object') {
      console.error(`[EnglishDataService] Error details:`, {
        message: error.message,
        name: error.name,
        stack: error.stack,
        response: error.response,
        status: error.status,
        code: error.code
      });
    }
  }

  /**
   * Try A2A with optional Supabase fallback
   */
  async tryWithFallback(a2aMethod, supabaseFallback, methodName) {
    // Try A2A first if enabled
    if (this.useA2A) {
      try {
        this.log(`${methodName}: Using A2A backend`);
        const result = await a2aMethod();
        this.log(`${methodName}: A2A success`);
        return result;
      } catch (error) {
        // Enhanced error logging with capability check
        const errorDetails = {
          error: error.message,
          name: error.name,
          code: error.code,
          isCapabilityNotFound: error.message && error.message.includes('CAPABILITY_NOT_FOUND'),
          isNetworkError: error.message && (
            error.message.includes('Failed to fetch') ||
            error.message.includes('ERR_CONNECTION_REFUSED') ||
            error.message.includes('NETWORK_ERROR') ||
            error.message.includes('Invalid URL') ||
            error.message.includes('CORS') ||
            error.message.includes('NetworkError')
          )
        };
        
        // Always log A2A errors with full details
        console.error(`[EnglishDataService] ${methodName}: A2A call failed`, errorDetails);
        
        // If capability not found, log available capabilities for debugging
        if (errorDetails.isCapabilityNotFound) {
          try {
            const capabilities = a2aEnglishService.getCapabilities();
            if (capabilities && Array.isArray(capabilities)) {
              const capabilityNames = capabilities.map(c => c.name || c).slice(0, 20);
              console.warn(`[EnglishDataService] Available A2A capabilities (first 20):`, capabilityNames);
              console.warn(`[EnglishDataService] Total capabilities: ${capabilities.length}`);
            } else {
              console.warn(`[EnglishDataService] Capabilities not loaded yet or invalid format`);
            }
          } catch (capError) {
            console.warn(`[EnglishDataService] Could not retrieve capabilities:`, capError.message);
          }
        }
        
        // Check if this is a connection error or backend configuration error
        const isConnectionError = errorDetails.isNetworkError;
        
        // Only log errors if not a connection/backend error, or if fallback is not available
        const fallbackAvailable = this.fallbackToSupabase && supabaseFallback;
        if (!isConnectionError || !fallbackAvailable) {
          this.logError(`${methodName}: A2A failed`, error);
        } else {
          // Connection/backend error with fallback available - log warning
          console.warn(`[EnglishDataService] ${methodName}: A2A backend error (${error.message}), using Supabase fallback`);
        }
        
        // Fallback to Supabase if enabled
        if (fallbackAvailable) {
          console.log(`[EnglishDataService] ${methodName}: Falling back to Supabase`);
          try {
            const result = await supabaseFallback();
            console.log(`[EnglishDataService] ${methodName}: Supabase fallback success`);
            return result;
          } catch (supabaseError) {
            this.logError(`${methodName}: Supabase fallback failed`, supabaseError);
            throw supabaseError;
          }
        }
        
        throw error;
      }
    }
    
    // Use Supabase directly if A2A is disabled
    if (supabaseFallback) {
      this.log(`${methodName}: A2A disabled, using Supabase`);
      return await supabaseFallback();
    }
    
    throw new Error(`${methodName}: A2A disabled and no Supabase fallback available`);
  }

  /**
   * A2A-only method (no fallback)
   */
  async a2aOnly(a2aMethod, methodName) {
    if (!this.useA2A) {
      throw new Error(`${methodName}: A2A backend is required but disabled`);
    }

    try {
      this.log(`${methodName}: Using A2A backend (A2A-only)`);
      const result = await a2aMethod();
      this.log(`${methodName}: A2A success`);
      return result;
    } catch (error) {
      this.logError(`${methodName}: A2A failed (A2A-only, no fallback)`, error);
      throw error;
    }
  }

  // ============================================================================
  // ESS Toolbox Methods
  // ============================================================================

  /**
   * Get ESS sample passages
   * Try A2A, fallback to Supabase
   */
  async getESSSamplePassages(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getESSSamplePassages(params);
        return result.data || result;
      },
      // Supabase fallback
      async () => {
        const passages = await supabaseServices.essToolboxService.getSamplePassages();
        return {
          data: passages,
          pagination: {
            page: params.page || 1,
            limit: params.limit || 20,
            total: passages.length,
            totalPages: Math.ceil(passages.length / (params.limit || 20))
          }
        };
      },
      'getESSSamplePassages'
    );
  }

  /**
   * Get ESS sentence patterns
   * Try A2A, fallback to Supabase
   */
  async getESSSentencePatterns() {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getESSSentencePatterns();
        return result.data || result.patterns || result;
      },
      // Supabase fallback
      async () => {
        return await supabaseServices.essToolboxService.getSentencePatterns();
      },
      'getESSSentencePatterns'
    );
  }

  /**
   * Get ESS Toolbox all data (combined)
   * Try A2A, fallback to Supabase
   */
  async getESSAllData() {
    return await this.tryWithFallback(
      // A2A - combine multiple calls
      async () => {
        const [samplePassages, sentencePatterns] = await Promise.all([
          a2aEnglishService.getESSSamplePassages({ limit: 100 }),
          a2aEnglishService.getESSSentencePatterns()
        ]);
        return {
          samplePassages: samplePassages.data || samplePassages.passages || samplePassages,
          sentencePatterns: sentencePatterns.data || sentencePatterns.patterns || sentencePatterns
        };
      },
      // Supabase fallback
      async () => {
        return await supabaseServices.essToolboxService.getAllData();
      },
      'getESSAllData'
    );
  }

  /**
   * Get DeepSeek stories from ESS Toolbox
   * Try A2A, fallback to Supabase
   */
  async getESSDeepSeekStories(params = {}) {
    return await this.tryWithFallback(
      // A2A - Use dedicated capability
      async () => {
        const result = await a2aEnglishService.getESSDeepSeekStories(params);
        return result.data || result.stories || result || [];
      },
      // Supabase fallback
      async () => {
        return await supabaseServices.essToolboxService.getDeepSeekStories();
      },
      'getESSDeepSeekStories'
    );
  }

  /**
   * Save ESS story
   * A2A only
   */
  async saveESSStory(storyData) {
    return await this.a2aOnly(
      async () => {
        const { storyId, title, category, difficulty, content, raw } = storyData;
        return await a2aEnglishService.saveESSStory(title, content, {
          storyId: storyId,
          category: category || 'deepseek_story',
          difficulty: difficulty || null,
          metadata: raw
        });
      },
      'saveESSStory'
    );
  }

  /**
   * Analyze ESS structure
   * A2A only (new feature)
   */
  async analyzeESSStructure(passage, options = {}) {
    return await this.a2aOnly(
      async () => {
        return await a2aEnglishService.analyzeESSStructure(passage, options);
      },
      'analyzeESSStructure'
    );
  }

  // ============================================================================
  // Knowledge Graph Methods
  // ============================================================================

  /**
   * Get knowledge graph stories
   * Try A2A, fallback to Supabase
   */
  async getKnowledgeGraphStories(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getKnowledgeGraphStories(params);
        return result.data || result;
      },
      // Supabase fallback (if available)
      async () => {
        // Check if knowledgeGraphService exists
        if (supabaseServices.knowledgeGraphService) {
          const stories = await supabaseServices.knowledgeGraphService.getStories(params);
          return {
            data: stories,
            pagination: {
              page: params.page || 1,
              limit: params.limit || 20,
              total: stories.length,
              totalPages: Math.ceil(stories.length / (params.limit || 20))
            }
          };
        }
        throw new Error('Supabase knowledge graph service not available');
      },
      'getKnowledgeGraphStories'
    );
  }

  /**
   * Get specific knowledge graph story by ID
   * Try A2A, fallback to Supabase
   */
  async getKnowledgeGraphStory(storyId) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getKnowledgeGraphStory(storyId);
        return result.data || result;
      },
      // Supabase fallback (if available)
      async () => {
        // Check if knowledgeGraphService exists
        if (supabaseServices.knowledgeGraphService) {
          const story = await supabaseServices.knowledgeGraphService.getStory(storyId);
          return {
            success: true,
            story: story
          };
        }
        throw new Error('Supabase knowledge graph service not available');
      },
      'getKnowledgeGraphStory'
    );
  }

  // ============================================================================
  // Vocabulary Methods
  // ============================================================================

  /**
   * Get vocabulary sets
   * A2A only
   */
  async getVocabularySets(params = {}) {
    return await this.a2aOnly(
      async () => {
        const result = await a2aEnglishService.getVocabularySets(params);
        return result.data || result;
      },
      'getVocabularySets'
    );
  }

  // ============================================================================
  // Writing Tools Methods
  // ============================================================================

  /**
   * Save writing composition
   * A2A only (no Supabase fallback - service doesn't exist)
   */
  async saveWritingComposition(compositionData) {
    return await this.a2aOnly(
      async () => {
        return await a2aEnglishService.saveWritingComposition(compositionData);
      },
      'saveWritingComposition'
    );
  }

  // ============================================================================
  // HKDSE Grammar Methods
  // ============================================================================

  /**
   * Get HKDSE grammar categories
   * Try A2A, fallback to Supabase
   */
  async getGrammarCategories() {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getGrammarCategories();
        return result.data || result.categories || result;
      },
      // Supabase fallback
      async () => {
        return await supabaseServices.hkdseGrammarService.getGrammarCategories();
      },
      'getGrammarCategories'
    );
  }

  /**
   * Get parts of speech data
   * Try A2A, fallback to Supabase
   */
  async getPartsOfSpeechData() {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getPartsOfSpeech();
        return result.data || result.parts_of_speech || result;
      },
      // Supabase fallback
      async () => {
        return await supabaseServices.hkdseGrammarService.getPartsOfSpeechData();
      },
      'getPartsOfSpeechData'
    );
  }

  /**
   * Get verb tenses data
   * Try A2A, fallback to Supabase
   */
  async getVerbTensesData() {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getVerbTenses();
        return result.data || result.verb_tenses || result;
      },
      // Supabase fallback
      async () => {
        return await supabaseServices.hkdseGrammarService.getVerbTensesData();
      },
      'getVerbTensesData'
    );
  }

  /**
   * Get sentence structure data
   * Try A2A, fallback to Supabase
   */
  async getSentenceStructureData() {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getSentenceStructure();
        return result.data || result.sentence_structure || result;
      },
      // Supabase fallback
      async () => {
        return await supabaseServices.hkdseGrammarService.getSentenceStructureData();
      },
      'getSentenceStructureData'
    );
  }

  /**
   * Get HKDSE exam tips
   * Try A2A, fallback to Supabase
   */
  async getHKDSEExamTips() {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getExamTips();
        return result.data || result.exam_tips || result;
      },
      // Supabase fallback
      async () => {
        return await supabaseServices.hkdseGrammarService.getHKDSEExamTips();
      },
      'getHKDSEExamTips'
    );
  }

  /**
   * Get clauses and phrases data
   * Try A2A, fallback to Supabase
   */
  async getClausesPhrasesData() {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getClausesPhrases();
        return result.data || result.clauses_phrases || result;
      },
      // Supabase fallback
      async () => {
        if (supabaseServices.hkdseGrammarService) {
          return await supabaseServices.hkdseGrammarService.getClausesPhrasesData();
        }
        throw new Error('Supabase HKDSE grammar service not available');
      },
      'getClausesPhrasesData'
    );
  }

  /**
   * Get common errors data
   * Try A2A, fallback to Supabase
   */
  async getCommonErrorsData() {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getCommonErrors();
        return result.data || result.common_errors || result;
      },
      // Supabase fallback
      async () => {
        if (supabaseServices.hkdseGrammarService) {
          return await supabaseServices.hkdseGrammarService.getCommonErrorsData();
        }
        throw new Error('Supabase HKDSE grammar service not available');
      },
      'getCommonErrorsData'
    );
  }

  /**
   * Get practice exercises data
   * Try A2A, fallback to Supabase
   */
  async getPracticeExercisesData() {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getPracticeExercises();
        return result.data || result.practice_exercises || result;
      },
      // Supabase fallback
      async () => {
        if (supabaseServices.hkdseGrammarService) {
          return await supabaseServices.hkdseGrammarService.getPracticeExercisesData();
        }
        throw new Error('Supabase HKDSE grammar service not available');
      },
      'getPracticeExercisesData'
    );
  }

  // ============================================================================
  // Linguistic Analysis Methods
  // ============================================================================

  /**
   * Get linguistic analysis examples
   * Try A2A, fallback to Supabase
   */
  async getLinguisticExamples(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getLinguisticExamples(params);
        // Handle various response formats
        if (result && result.success !== false) {
          // A2A protocol response: { success: true, data: { examples: [...] } }
          if (result.data) {
            if (Array.isArray(result.data)) {
              return result.data;
            } else if (result.data.examples && Array.isArray(result.data.examples)) {
              return result.data.examples;
            } else if (result.data.data && Array.isArray(result.data.data)) {
              return result.data.data;
            }
          }
          // Direct examples array
          if (Array.isArray(result.examples)) {
            return result.examples;
          }
          // Direct array response
          if (Array.isArray(result)) {
            return result;
          }
          // Return as-is if it's an object with examples
          return result;
        }
        throw new Error('Invalid A2A response format for linguistic examples');
      },
      // Supabase fallback
      async () => {
        return await supabaseServices.linguisticAnalysisService.getAllExamples();
      },
      'getLinguisticExamples'
    );
  }

  /**
   * Get specific linguistic analysis example
   * Try A2A, fallback to Supabase
   */
  async getLinguisticExample(exampleId) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishService.getLinguisticExample(exampleId);
        return result.data || result.example || result;
      },
      // Supabase fallback
      async () => {
        return await supabaseServices.linguisticAnalysisService.getExample(exampleId);
      },
      'getLinguisticExample'
    );
  }

  // ============================================================================
  // GrammarSpeak Methods
  // ============================================================================

  /**
   * Get grammar topics (GrammarSpeak)
   * A2A only (no Supabase fallback - service doesn't exist)
   */
  async getGrammarTopics(params = {}) {
    return await this.a2aOnly(
      async () => {
        const result = await a2aEnglishService.getGrammarTopics(params);
        return result.data || result;
      },
      'getGrammarTopics'
    );
  }

  // ============================================================================
  // DeepSeek AI Methods
  // ============================================================================

  /**
   * DeepSeek AI chat
   * A2A only (centralized proxy)
   */
  async deepseekChat(message, options = {}) {
    return await this.a2aOnly(
      async () => {
        return await a2aEnglishService.deepseekChat(message, options);
      },
      'deepseekChat'
    );
  }

  // ============================================================================
  // UX Negotiation Methods
  // ============================================================================

  /**
   * Negotiate UX format
   * Try A2A, return default if disabled
   */
  async negotiateUX(context = null) {
    if (!this.useA2A) {
      this.log('negotiateUX: A2A disabled, returning default UX format');
      // Return default UX format
      return {
        format: 'default',
        device: context?.device || 'desktop',
        screenWidth: context?.screenWidth || 1920,
        screenHeight: context?.screenHeight || 1080,
        recommendations: {
          layout: 'standard',
          imageQuality: 'high',
          animationLevel: 'full'
        }
      };
    }

    try {
      this.log('negotiateUX: Using A2A backend');
      const result = await a2aEnglishService.negotiateUX(context);
      this.log('negotiateUX: A2A success');
      return result;
    } catch (error) {
      this.logError('negotiateUX: A2A failed, returning default UX format', error);
      // Return default UX format on error
      return {
        format: 'default',
        device: context?.device || 'desktop',
        screenWidth: context?.screenWidth || 1920,
        screenHeight: context?.screenHeight || 1080,
        recommendations: {
          layout: 'standard',
          imageQuality: 'high',
          animationLevel: 'full'
        }
      };
    }
  }

  // ============================================================================
  // Cleanup Methods
  // ============================================================================

  /**
   * Cleanup - unsubscribe from all tasks
   */
  cleanup() {
    this.log('cleanup: Cleaning up A2A service');
    a2aEnglishService.cleanup();
  }
}

/**
 * Create and export singleton instance
 */
const englishDataService = new EnglishDataService();

export default englishDataService;

/**
 * Export class for testing or custom instances
 */
export { EnglishDataService };

